'use client';

import { KeyboardArrowDownRounded } from '@mui/icons-material';
import { Button, Menu, MenuItem } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import { Link } from '@/configurations/i18n/navigation';
import { trackNavigationClick } from '@/configurations/tracking/navigation';

type CatalogEntry = {
    /** Nom de la DMC : un nom propre, donc jamais traduit. */
    name: string;
    href: string;
};

type Props = {
    entries: readonly CatalogEntry[];
    isOverlay: boolean;
};

/**
 * Entrée « Mon catalogue » d'un DMC multi-fiches : un seul point d'entrée dans la barre, qui
 * déroule une fiche par ligne. Aligner N liens à plat noyait la navigation dès trois fiches, et
 * un lien unique aurait rendu les autres fiches inatteignables.
 */
export function MyCatalogMenuButton({ entries, isOverlay }: Props) {
    const t = useTranslations('nav');
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const isOpen = Boolean(anchorEl);

    return (
        <>
            <Button
                variant="text"
                onClick={(event) => setAnchorEl(event.currentTarget)}
                aria-haspopup="menu"
                aria-expanded={isOpen}
                endIcon={
                    <KeyboardArrowDownRounded
                        sx={{
                            fontSize: 16,
                            transition: 'transform 200ms ease',
                            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                    />
                }
                sx={(theme) => ({
                    fontWeight: 400,
                    fontSize: '1rem',
                    letterSpacing: '0.00938em',
                    lineHeight: 1.5,
                    px: 0,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    color: isOverlay
                        ? alpha(theme.palette.common.white, isOpen ? 0.95 : 0.8)
                        : isOpen
                          ? 'text.primary'
                          : 'text.secondary',
                    '&:hover': {
                        backgroundColor: 'transparent',
                        color: isOverlay ? 'common.white' : 'text.primary',
                    },
                })}
            >
                {t('myCatalog')}
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={isOpen}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{ paper: { sx: { minWidth: 200 } } }}
            >
                {entries.map(({ name, href }) => (
                    <MenuItem
                        key={href}
                        component={Link}
                        href={href}
                        onClick={() => {
                            trackNavigationClick({
                                navItem: 'my_catalog',
                                triggerContext: 'navbar',
                            });
                            setAnchorEl(null);
                        }}
                    >
                        {name}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}
